import { useState } from 'react';
import businessPartner from '@/@core/modules/business-partner/infra/container.registry';
import { Notification } from '@presentation/components/common';
import { getNotFoundMessage, getQueryErrorMessage } from '@/@presentation/constants/businessPartnerMessages';
import { BusinessPartnerAddress } from '@/@core/modules/business-partner/domain/business-partner.entities';
import { normalizeCnpj } from '@/@core/utils/cnpj.utils';

/**
 * Hook customizado para validação de Business Partner (SAP)
 * 
 * Centraliza a lógica de validação de tomadores PJ para evitar duplicação de código.
 * 
 * Regras de negócio:
 * - CPF: Retorna true diretamente (sem chamar API)
 * - CNPJ: Valida no SAP via API
 *   - true: Tomador cadastrado (permite prosseguir)
 *   - false: Tomador não cadastrado (bloqueia)
 *   - exception: Erro de sistema (bloqueia)
 * 
 * @returns {object} Objeto com função de validação e estado de erro
 */
export const useBusinessPartner = () => {
    const [isBusinessPartnerError, setIsBusinessPartnerError] = useState(false);

    /**
     * Valida se o tomador está cadastrado no SAP
     * Apenas para CNPJ - CPF retorna true automaticamente
     * 
     * @param params Parâmetros para validação
     * @param params.name Nome do tomador
     * @param params.document Documento (CPF/CNPJ) com ou sem máscara
     * @param params.scheduleId ID do agendamento (opcional)
     * @param params.options Opções adicionais
     * @param params.options.buttonType Tipo de botão que iniciou validação ('nf' ou 'payment')
     * @param params.options.itemIds IDs dos itens selecionados (para mensagens de erro)
     * @param params.options.onError Callback opcional executado quando houver erro
     * @param params.options.showNotification Se false, não exibe notificação automática (padrão: true)
     * @returns Promise<boolean> true se pode prosseguir, false se deve bloquear
     */
    const validateBusinessPartner = async (params: {
        name: string;
        document: string;
        scheduleId?: string;
        address?: BusinessPartnerAddress;
        options?: {
            buttonType?: 'nf' | 'payment';
            itemIds?: number[];
            onError?: () => void;
            onSuccess?: () => void;
            showNotification?: boolean;
            totalSelected?: number;
        };
    }): Promise<boolean> => {
        try {
            setIsBusinessPartnerError(false);
            
            // Remover apenas pontuação de máscara (./-), preservando letras do CNPJ alfanumérico
            const documentoLimpo = normalizeCnpj(params.document ?? '');
            
            // Verificar se é CPF (não precisa validar no SAP)
            if (documentoLimpo.length <= 11) {
                // CPF - retornar true sem chamar API
                if (params.options?.onSuccess) {
                    params.options.onSuccess();
                }
                return true;
            }
            
            // É CNPJ - chamar API para validar no SAP e atualizar endereço
            const isRegistered = await businessPartner.getBusinessPartner.execute({
                name: params.name,
                cnpj: documentoLimpo,
                scheduleId: params.scheduleId,
                address: params.address,
            });
            
            // Verificar resposta booleana da API
            if (isRegistered === true) {
                // Tomador cadastrado - pode prosseguir
                if (params.options?.onSuccess) {
                    params.options.onSuccess();
                }
                return true;
            }

            // Tomador não cadastrado - exibir erro apenas se showNotification não for false
            const showNotification = params.options?.showNotification !== false;
            if (showNotification) {
                const errorMsg = getNotFoundMessage(params.options?.itemIds, params.options?.totalSelected);
                Notification.error(errorMsg);
            }
            setIsBusinessPartnerError(true);

            // Executar callback de erro se fornecido
            if (params.options?.onError) {
                params.options.onError();
            }
            
            return false;
            
        } catch (error) {
            // Erro de sistema - exibir mensagem de falha na consulta
            const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
            
            // Log estruturado para observabilidade
            console.error('Erro ao consultar tomador PJ:', {
                error: errorMessage,
                scheduleId: params.scheduleId,
                timestamp: new Date().toISOString(),
            });

            const showNotification = params.options?.showNotification !== false;
            if (showNotification) {
                const errorMsg = getQueryErrorMessage();
                Notification.error({
                    ...errorMsg,
                    description: errorMessage,
                });
            }

            setIsBusinessPartnerError(true);
            
            // Executar callback de erro se fornecido
            if (params.options?.onError) {
                params.options.onError();
            }
            
            return false;
        }
    };

    /**
     * Reseta o estado de erro
     */
    const resetBusinessPartnerError = () => {
        setIsBusinessPartnerError(false);
    };

    return {
        validateBusinessPartner,
        isBusinessPartnerError,
        resetBusinessPartnerError,
    };
};


